import { useEffect, useRef, useState } from "react";
import "../../../css/Checks/Check.css";
import "../../../css/Buttons/RadioButton.css";

import SecondaryButton from "../../Components/Buttons/SecondaryButton";
import Banner from "../../Components/Banners/Banner";
import SubmissionPopUp from "../../Components/PopUp/SubmissionPopUp";

import { ReactComponent as PersonalInformationIcon } from "../../../assets/icons/personalInformation.svg";
import { ReactComponent as AddressIcon } from "../../../assets/icons/currentAddress.svg";

import TextInput from "../../Components/Inputs/TextInput";
import DateInput from "../../Components/Inputs/DateInput";
import SearchInput from "../../Components/Inputs/SearchInput";

import { isValidForm, buildPayload } from "../utils/amlForm";
import { makeHandleChange, makeDobChange } from "../utils/factories/factories";
import {
  initialSimpleState,
  validateSimple,
} from "../utils/checkStates/simpleState";
import { useNavigate } from "react-router-dom";
import { useAdminMails as useAdminAdviserMails } from "../../../context/AdminEmailsContext";
import { useUserEmail } from "../../../context/UserEmailContext";

import { addDoc, collection, doc, setDoc, updateDoc } from "firebase/firestore";
import { auth, firestore as dbAML } from "../../../backend/firebaseConfig";
import { getEndUserId } from "../utils/helperFunctions/getEndUserId";

import {
  getAddressAutocomplete,
  getAddressById,
} from "../utils/postcodes/getAddressClient";


const SimpleCheck = () => {
  const { userEmail } = useUserEmail();
  const { currentAdviserEmail } = useAdminAdviserMails();
  const AML_SERVER = process.env.REACT_APP_AML_SERVER;

  const [formData, setFormData] = useState(initialSimpleState);
  const [errors, setErrors] = useState({});
  const [resetSeed, setResetSeed] = useState(0);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmissionPopUp, setShowSubmissionPopUp] = useState(false);

  const handleChange = makeHandleChange(setFormData, setErrors);
  const handleDobChange = makeDobChange(setFormData, setErrors);
   const navigate = useNavigate();

   // ---------------- Address Search (getAddress.io) ----------------
   const [postcodeSearch, setPostcodeSearch] = useState(
     initialSimpleState?.address?.postcode || ""
   );

   // separate variable for API search term
   const [postcodeSearchTerm, setPostcodeSearchTerm] = useState("");

   const [addrState, setAddrState] = useState({ state: "idle", msg: "" });
   const [addrSuggestions, setAddrSuggestions] = useState([]);
   const [showAddrDropdown, setShowAddrDropdown] = useState(false);

   const addrDebounceRef = useRef(null);
   const autoAbortRef = useRef(null);
   const getAbortRef = useRef(null);

   const resetAll = () => {
     setFormData(initialSimpleState);
     setErrors({});
     setResetSeed((n) => n + 1);

     // reset address search UI too
     setPostcodeSearch("");
     setPostcodeSearchTerm("");
     setAddrSuggestions([]);
     setShowAddrDropdown(false);
     setAddrState({ state: "idle", msg: "" });
   };

   const cancel = (ref) => {
     try {
       ref.current?.abort?.();
     } catch {}
   };

   // keep the SearchInput synced with formData.postcode (e.g. after selecting an address)
   useEffect(() => {
     const pc = formData.address.postcode || "";
     setPostcodeSearch(pc);
     setPostcodeSearchTerm(pc);
   }, [formData.address.postcode]);

   const runAutocomplete = async (term) => {
     const q = String(term || "").trim();

     if (q.length < 2) {
       setAddrSuggestions([]);
       setShowAddrDropdown(false);
       setAddrState({ state: "idle", msg: "" });
       return;
     }

     cancel(autoAbortRef);
     const ac = new AbortController();
     autoAbortRef.current = ac;

     setAddrState({ state: "loading", msg: "Searching addresses..." });

     const out = await getAddressAutocomplete(q, { signal: ac.signal });

     //  ignore aborted requests
     if (out?.aborted || ac.signal.aborted) return;

     if (!out.ok) {
       setAddrSuggestions([]);
       setShowAddrDropdown(false);
       setAddrState({
         state: "error",
         msg: out.error || "Autocomplete failed",
       });
       return;
     }

     const list = out.suggestions || [];
     setAddrSuggestions(list);
     setShowAddrDropdown(list.length > 0);
     setAddrState({
       state: "success",
       msg: list.length ? `Found ${list.length} results` : "No results",
     });
   };

   const applyAddressFromId = async (id) => {
     cancel(getAbortRef);
     const ac = new AbortController();
     getAbortRef.current = ac;

     setAddrState({ state: "loading", msg: "Fetching address..." });

     const out = await getAddressById(id, { signal: ac.signal });

     if (!out.ok) {
       setAddrState({
         state: "error",
         msg: out.error || "Failed to fetch address",
       });
       return;
     }

     const a = out.address || {};

     // Map to your form fields
     handleChange("address", "buildingNumber", "text")(a.building_number || "");
     handleChange("address", "buildingName", "text")(a.building_name || "");
     handleChange(
       "address",
       "street",
       "text"
     )(a.thoroughfare || a.line_1 || "");
     handleChange("address", "town", "text")(a.town_or_city || "");
     handleChange("address", "county", "text")(a.county || "");

     const nextPc =
       a.postcode || formData.address.postcode || postcodeSearch || "";

     // update both UI + state (no normalisation)
     handleChange("address", "postcode", "text")(nextPc);
     setPostcodeSearch(nextPc);
     setPostcodeSearchTerm(nextPc);

     setShowAddrDropdown(false);
     setAddrSuggestions([]);
     setAddrState({ state: "success", msg: "Address selected" });
   };

   useEffect(() => {
     return () => {
       cancel(autoAbortRef);
       cancel(getAbortRef);
       if (addrDebounceRef.current) clearTimeout(addrDebounceRef.current);
     };
   }, []);

   // ---------------- helpers ----------------
   const flattenToNested = (flat) => {
     const nested = {};
     Object.entries(flat || {}).forEach(([path, msg]) => {
       const parts = path.split(".");
       let cur = nested;
       for (let i = 0; i < parts.length; i++) {
         const key = parts[i];
         if (i === parts.length - 1) cur[key] = msg;
         else {
           cur[key] = cur[key] || {};
           cur = cur[key];
         }
       }
     });
     return nested;
   };

   // ---------------- Submit ----------------
   const handleSubmit = async () => {
     const nextErrors = validateSimple(formData);
     setErrors(nextErrors);

     if (!isValidForm(nextErrors)) return;
     if (isSubmitting) return;

     const endUserId = getEndUserId(auth.currentUser?.uid);
     const base = (AML_SERVER || "").replace(/\/$/, "");
     if (!base) {
       setErrors((p) => ({ ...p, _server: "AML server URL missing" }));
       return;
     }

     const payload = {
       person: {
         firstName: formData.personal.firstName,
         lastName: formData.personal.lastName,
         dob: formData.personal.dob,
       },
       address: {
         buildingNumber: formData.address.buildingNumber || "",
         buildingName: formData.address.buildingName || "",
         street: formData.address.street,
         town: formData.address.town,
         county: formData.address.county,
         postcode: formData.address.postcode,
       },
     };

     let docRef;
     let vendorData;

     try {
       setIsSubmitting(true);

       docRef = await addDoc(
         collection(dbAML, "AML Checks", userEmail, "Checks"),
         {
           checkType: "Simple",
           createdAt: new Date().toISOString(),
           createdBy: userEmail || null,
           forAdviser: currentAdviserEmail || null,
         },
       );

       vendorData = docRef.id;

       setShowSubmissionPopUp(true);

       await updateDoc(docRef, {
         vendorData,
         clientReferenceId: vendorData,
         endUserId: endUserId || "",
         experianStatus: "processing",
         updatedAt: new Date().toISOString(),
       });

       const simplePayloadfb = buildPayload(formData, "Simple");
       await addDoc(collection(docRef, "Request Data"), {
         ...simplePayloadfb,
         endUserId: endUserId || "",
         createdAt: new Date().toISOString(),
       });

       const startRes = await fetch(`${base}/simple-check`, {
         method: "POST",
         headers: {
           "Content-Type": "application/json",
           Accept: "application/json",
         },
         body: JSON.stringify({
           data: payload,
           vendorData,
           endUserId: endUserId || "",
           userEmail: userEmail || "",
         }),
       });

       let startJson = null;
       if (startRes.status === 200) startJson = await startRes.json();

       if (startRes.status !== 200) {
         await updateDoc(docRef, {
           experianStatus: "failed",
           updatedAt: new Date().toISOString(),
         });
         //  resetAll();
         return;
       }

       if (!startRes.ok) {
         await updateDoc(docRef, {
           experianStatus: "failed",
           errorMessage:
             startJson?.message || startJson?.error || "Simple check failed",
           updatedAt: new Date().toISOString(),
         });

         if (startJson?.fieldErrors) {
           setErrors((prev) => ({
             ...prev,
             ...flattenToNested(startJson.fieldErrors),
           }));
         } else {
           setErrors((p) => ({
             ...p,
             _server:
               startJson?.message ||
               startJson?.error ||
               "Failed to run simple check",
           }));
         }
         //  resetAll();
         return;
       }

       const expRequestId = startJson?.expRequestId || null;
       const expDocRef = doc(docRef, "Experian Response", "Response");

       await setDoc(
         expDocRef,
         {
           experianStatus: startJson?.experianStatus || "completed",
           expRequestId,
           response: startJson,
           updatedAt: new Date().toISOString(),
           updatedBy: "frontend",
         },
         { merge: true },
       );

       await updateDoc(docRef, {
         experianStatus: "completed",
         expRequestId,
         updatedAt: new Date().toISOString(),
       });

       setFormData(initialSimpleState);
       setErrors({});
       setResetSeed((n) => n + 1);

       // reset address search UI too
       setPostcodeSearch("");
       setPostcodeSearchTerm("");
       setAddrSuggestions([]);
       setShowAddrDropdown(false);
       setAddrState({ state: "idle", msg: "" });
     } catch (err) {
       if (docRef) {
         try {
           await updateDoc(docRef, {
             experianStatus: "failed",
             errorMessage: err?.message || "Failed to run simple check",
             updatedAt: new Date().toISOString(),
           });
         } catch {}
       }

       setErrors((p) => ({
         ...p,
         _server: err?.message || "Failed to run simple check",
       }));
     } finally {
       setIsSubmitting(false);
       resetAll();
      //  navigate("/my-checks");
     }
   };

   return (
     <div className="Check-Container">
       {/* SFL PROTECTION ONLY-No tool tip */}
       {/* <Banner
        type="info"
        message="Performed only on existing clients who’ve completed ID checks previously and the same details. This will confirm their PEP and Sanctions status remains unchanged."
      /> */}
       <div className="Check-Content">
         {/* Personal Information */}
         <div className="Check-Section">
           <div className="Check-Section-Header">
             <PersonalInformationIcon />
             <div className="AML-Text-Input primary">Personal Information</div>
           </div>

           <div className="Check-Section-Inputs">
             <TextInput
               key={`fn-${resetSeed}`}
               label="First Name"
               value={formData.personal.firstName}
               isRequired
               onChange={handleChange("personal", "firstName", "text")}
               placeholder="Alex"
               error={errors?.personal?.firstName}
             />
             <TextInput
               key={`ln-${resetSeed}`}
               label="Last Name"
               value={formData.personal.lastName}
               isRequired
               onChange={handleChange("personal", "lastName", "text")}
               placeholder="Smith"
               error={errors?.personal?.lastName}
             />
             <DateInput
               key={`dob-${resetSeed}`}
               label="Date of Birth"
               value={formData.personal.dob}
               isRequired
               onChange={handleDobChange}
               placeholderText="DD/MM/YYYY"
               dateFormat="DD/MM/YYYY"
               isError={errors?.personal?.dob}
             />
           </div>
         </div>

         <div className="Horizontal-Line" />

         {/* Current Address */}
         <div className="Check-Section">
           <div className="Check-Section-Header">
             <AddressIcon />
             <div className="AML-Text-Input primary">Current Address</div>
           </div>

           <Banner
             type="info"
             message="Please enter a valid UK address, including the building number or name."
           />

           {/* Address Search (replaces typed postcode box) */}
           <div className="aml-postcode-wrapper">
             <SearchInput
               label="Address Lookup"
               placeholder="Start typing the address or postcode..."
               value={postcodeSearch}
               onChange={(val) => {
                 setPostcodeSearch(val);

                 // keep formData in sync (do NOT normalise)
                 handleChange("address", "postcode", "text")(val);

                 // separate search term used for API calls
                 setPostcodeSearchTerm(val);

                 if (addrDebounceRef.current)
                   clearTimeout(addrDebounceRef.current);
                 addrDebounceRef.current = setTimeout(() => {
                   runAutocomplete(val);
                 }, 300);
               }}
               onSubmit={() => runAutocomplete(postcodeSearchTerm)}
             />

             {/* <div className="aml-postcode-actions">
              <SecondaryButton
                label={
                  addrState.state === "loading" ? "Searching..." : "Lookup"
                }
                onClick={() => runAutocomplete(postcodeSearchTerm)}
                isDisabled={addrState.state === "loading"}
              />

              {addrState.state === "error" && (
                <div className="aml-postcode-error">{addrState.msg}</div>
              )}
              {addrState.state === "success" && (
                <div className="aml-postcode-success">{addrState.msg}</div>
              )}
              {addrState.state === "loading" && (
                <div className="aml-postcode-help">Searching…</div>
              )}
            </div> */}

             {showAddrDropdown && addrSuggestions.length > 0 && (
               <div className="aml-postcode-dropdown">
                 {addrSuggestions.map((sug) => (
                   <div
                     key={sug.id}
                     className="aml-postcode-option AML-Text-Body"
                     onMouseDown={(ev) => {
                       ev.preventDefault();
                       applyAddressFromId(sug.id);
                     }}
                   >
                     {sug.address}
                   </div>
                 ))}
               </div>
             )}
           </div>

           {/* Address Fields */}
           <div className="Check-Section-Inputs">
             <TextInput
               key={`bnum-${resetSeed}`}
               label="Building Number"
               value={formData.address.buildingNumber}
               onChange={handleChange("address", "buildingNumber", "text")}
               placeholder="123"
               isRequired={false}
               error={errors?.address?.buildingNumber}
               maxLength={10}
             />
             <TextInput
               key={`bname-${resetSeed}`}
               label="Building Name"
               value={formData.address.buildingName}
               onChange={handleChange("address", "buildingName", "text")}
               placeholder="High Tower"
               isRequired={false}
               error={errors?.address?.buildingName}
             />
             <TextInput
               key={`street-${resetSeed}`}
               label="Street"
               value={formData.address.street}
               isRequired
               onChange={handleChange("address", "street", "text")}
               placeholder="High Street"
               error={errors?.address?.street}
             />
           </div>

           <div className="Check-Section-Inputs">
             <TextInput
               key={`town-${resetSeed}`}
               label="Town"
               value={formData.address.town}
               isRequired
               onChange={handleChange("address", "town", "text")}
               placeholder="London"
               error={errors?.address?.town}
             />
             <TextInput
               key={`county-${resetSeed}`}
               label="County"
               value={formData.address.county}
               isRequired
               onChange={handleChange("address", "county", "text")}
               placeholder="Greater London"
               error={errors?.address?.county}
             />
             <TextInput
               label="Postcode"
               value={formData.address.postcode}
               isRequired
               onChange={(e) => {
                 // keep as-is (NO normalisation)
                 const typed = e.target.value;
                 handleChange("address", "postcode", "text")(typed);
                 setPostcodeSearch(typed);
                 setPostcodeSearchTerm(typed);
               }}
               placeholder="AB1 2CD"
               error={errors?.address?.postcode}
             />
           </div>
         </div>
       </div>
       <SecondaryButton
         label={isSubmitting ? "Submitting..." : "Submit"}
         onClick={handleSubmit}
         isDisabled={isSubmitting}
       />
       {showSubmissionPopUp && (
         <SubmissionPopUp
           onClose={() => {
             setShowSubmissionPopUp(false);
            //  navigate("/check²");
           }}
         />
       )}
     </div>
   );
};

export default SimpleCheck;
